<div class="min-h-screen flex items-center justify-center relative bg-cover bg-center bg-no-repeat p-4" [style.background-image]="'url(' + backgroundImageUrl + ')'">
  <div class="absolute inset-0 bg-white/10 backdrop-blur-[2px]"></div>
  
  <div class="relative z-10 w-full max-w-[450px]">
    <div class="bg-white rounded-xl p-10 sm:p-8 sm:px-6 shadow-2xl flex flex-col gap-6 relative overflow-visible">
      <!-- Logo -->
      <div class="flex justify-center items-center mt-0 p-0 relative z-10 min-h-[60px] sm:min-h-[50px] max-h-[80px] sm:max-h-[70px] overflow-visible w-full">
        <app-image-tag 
          [src]="logoUrl" 
          [alt]="'Platform Admin Logo'" 
          class="h-8 w-8"
          [preventDownload]="true">
        </app-image-tag>
      </div>

      @if (!passwordReset()) {
        <!-- Title -->
        <div class="text-center">
          <h1 class="text-2xl font-bold text-gray-900 mb-1">Reset Password</h1>
          <p class="text-sm text-gray-600">Platform Admin</p>
        </div>

        <!-- Reset Password Form -->
        <form [formGroup]="resetPasswordForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-6">
          <div class="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <p class="text-sm text-blue-700">
              Please enter your new password. Make sure it's at least 8 characters long.
            </p>
          </div>

          <!-- New Password Input -->
          <app-password
            label="New Password"
            [required]="true"
            placeholder="Enter new password"
            formControlName="newPassword"
            [toggleMask]="true"
            id="newPassword"
            name="newPassword">
          </app-password>

          <!-- Confirm Password Input -->
          <app-password
            label="Confirm New Password"
            [required]="true"
            placeholder="Confirm new password"
            formControlName="confirmPassword"
            [toggleMask]="true"
            id="confirmPassword"
            name="confirmPassword">
          </app-password>

          @if (resetPasswordForm.hasError('passwordMismatch') && resetPasswordForm.get('confirmPassword')?.touched) {
            <p class="text-xs text-red-500 font-medium">Passwords do not match</p>
          }

          <!-- Password Validation Rules -->
          <div class="password-validation">
            <div class="validation-title">Password must contain:</div>
            <div class="validation-rules">
              <div class="validation-rule" [class.valid]="getPasswordValidationStatus().minLength">
                <svg class="validation-icon" [class.text-red-500]="!getPasswordValidationStatus().minLength" [class.text-green-500]="getPasswordValidationStatus().minLength" fill="currentColor" viewBox="0 0 20 20">
                  @if (getPasswordValidationStatus().minLength) {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  } @else {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  }
                </svg>
                <span [class.text-red-500]="!getPasswordValidationStatus().minLength" [class.text-green-500]="getPasswordValidationStatus().minLength">Minimum 8 characters</span>
              </div>
              <div class="validation-rule" [class.valid]="getPasswordValidationStatus().hasUppercase">
                <svg class="validation-icon" [class.text-red-500]="!getPasswordValidationStatus().hasUppercase" [class.text-green-500]="getPasswordValidationStatus().hasUppercase" fill="currentColor" viewBox="0 0 20 20">
                  @if (getPasswordValidationStatus().hasUppercase) {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  } @else {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  }
                </svg>
                <span [class.text-red-500]="!getPasswordValidationStatus().hasUppercase" [class.text-green-500]="getPasswordValidationStatus().hasUppercase">At least 1 uppercase alphabet</span>
              </div>
              <div class="validation-rule" [class.valid]="getPasswordValidationStatus().hasLowercase">
                <svg class="validation-icon" [class.text-red-500]="!getPasswordValidationStatus().hasLowercase" [class.text-green-500]="getPasswordValidationStatus().hasLowercase" fill="currentColor" viewBox="0 0 20 20">
                  @if (getPasswordValidationStatus().hasLowercase) {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  } @else {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  }
                </svg>
                <span [class.text-red-500]="!getPasswordValidationStatus().hasLowercase" [class.text-green-500]="getPasswordValidationStatus().hasLowercase">At least 1 lowercase alphabet</span>
              </div>
              <div class="validation-rule" [class.valid]="getPasswordValidationStatus().hasNumber">
                <svg class="validation-icon" [class.text-red-500]="!getPasswordValidationStatus().hasNumber" [class.text-green-500]="getPasswordValidationStatus().hasNumber" fill="currentColor" viewBox="0 0 20 20">
                  @if (getPasswordValidationStatus().hasNumber) {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  } @else {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  }
                </svg>
                <span [class.text-red-500]="!getPasswordValidationStatus().hasNumber" [class.text-green-500]="getPasswordValidationStatus().hasNumber">At least 1 number</span>
              </div>
              <div class="validation-rule" [class.valid]="getPasswordValidationStatus().hasSpecialChar">
                <svg class="validation-icon" [class.text-red-500]="!getPasswordValidationStatus().hasSpecialChar" [class.text-green-500]="getPasswordValidationStatus().hasSpecialChar" fill="currentColor" viewBox="0 0 20 20">
                  @if (getPasswordValidationStatus().hasSpecialChar) {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  } @else {
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  }
                </svg>
                <span [class.text-red-500]="!getPasswordValidationStatus().hasSpecialChar" [class.text-green-500]="getPasswordValidationStatus().hasSpecialChar">At least 1 special character</span>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <app-button
            type="submit"
            variant="primary"
            [fullWidth]="true"
            [loading]="isLoading()"
            [disabled]="isLoading() || !token()">
            {{ isLoading() ? 'Resetting...' : 'Reset Password' }}
          </app-button>

          <!-- Back to Login Link -->
          <a 
            (click)="goToLogin(); $event.preventDefault()" 
            class="text-center text-primary-600 underline text-sm cursor-pointer transition-colors duration-200 hover:text-primary-700">
            Back to Login
          </a>
        </form>
      } @else {
        <!-- Success Message -->
        <div class="flex flex-col items-center gap-4 text-center">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900 mb-2">Password Reset Successful!</h2>
            <p class="text-sm text-gray-600 mb-4">
              Your password has been reset successfully. You will be redirected to the login page shortly.
            </p>
          </div>
          <app-button
            type="button"
            variant="primary"
            [fullWidth]="true"
            (click)="goToLogin()">
            Go to Login
          </app-button>
        </div>
      }

      <!-- Footer -->
      <div class="flex flex-col items-center gap-2 mt-4 pt-4 border-t border-gray-200">
        <app-image-tag 
          [src]="logoUrl" 
          [alt]="'Platform Admin Logo'" 
          class="h-8 w-8 object-contain"
          [preventDownload]="true">
        </app-image-tag>
        <p class="text-xs text-gray-500 m-0">Powered by HCMS Platform</p>
      </div>
    </div>
  </div>
</div>


